import type { ReactNode } from 'react';

/**
 * The content band (§5.2, non-negotiable §9).
 *
 * > Recall-driven elements may span the screen. Search-driven elements must not.
 *
 * A key is recall: spelling is intact, QWERTZ is decades of muscle memory, and the person
 * does not visually hunt for "S". Predictions, buttons, status messages and the output text
 * are search — they have to be *noticed and located* — so they live in the middle 60 % of the
 * width, which is the region visible under a left *or* right field loss and also where a
 * low-acuity reader's usable central vision is.
 *
 * So: wrap anything search-driven in this. Do not wrap the keyboard.
 *
 * The offset exists so calibration can move the band away from a field loss without a code
 * change (§5.6). We ship centre and full only — the mirrored half-screen layouts stay unbuilt
 * until calibration data says which side to mirror to.
 */
export function ContentBand({
  children,
  as: Element = 'div',
  className,
  grow = false,
  style,
  ...rest
}: {
  children: ReactNode;
  as?: 'div' | 'nav' | 'section' | 'header' | 'footer';
  className?: string;
  /**
   * Stretch to fill the remaining height and become a flex column.
   *
   * Needed by any surface whose children size themselves against the available space — the phrase
   * grid, the pain scale, the body map. Without it the band has auto height, a child's `height:
   * 100%` resolves against nothing, and targets silently collapse to their content size. That is
   * not a cosmetic bug: it took the pain scale to 10 mm, well under the 16 mm interactive floor,
   * and it looked plausible on screen.
   */
  grow?: boolean;
} & React.HTMLAttributes<HTMLElement>): React.JSX.Element {
  return (
    <Element
      className={className ? `content-band ${className}` : 'content-band'}
      style={{
        ...(grow
          ? { flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' as const }
          : {}),
        ...style,
      }}
      {...rest}
    >
      {children}
    </Element>
  );
}
